import React from 'react';
import { useMode } from '@/contexts/ModeContext';
import { cn } from '@/lib/utils';
import { Home, Tractor } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import useTranslation from '@/hooks/use-translation';
import { Button } from './button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './tooltip';

interface SectorToggleProps {
  className?: string;
  showLabels?: boolean;
  variant?: 'default' | 'minimal';
}

const SectorToggle = ({ 
  className, 
  showLabels = true,
  variant = 'default'
}: SectorToggleProps) => {
  const { mode, toggleMode } = useMode();
  const { toast } = useToast();
  const { translate } = useTranslation();
  
  const handleToggle = () => {
    const newMode = mode === 'agriculture' ? 'household' : 'agriculture';
    toggleMode();
    
    toast({
      title: translate(
        newMode === 'agriculture' 
          ? "common.switchedToAgriculture" 
          : "common.switchedToHousehold"
      ),
      description: translate("common.dashboardUpdated"),
    });
  };

  if (variant === 'minimal') {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleToggle}
              className={cn(
                "transition-colors",
                mode === 'agriculture' ? "text-nature-600 hover:text-nature-700" : "text-primary hover:text-primary/80",
                className
              )}
            >
              {mode === 'agriculture' ? <Tractor size={20} /> : <Home size={20} />}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>
              {translate(
                mode === 'agriculture' 
                  ? "common.switchToHousehold" 
                  : "common.switchToAgriculture"
              )}
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  
  return (
    <div className={cn(
      "flex items-center gap-3",
      className
    )}>
      {showLabels && (
        <span className={cn(
          "text-sm font-medium transition-colors",
          mode === 'household' ? "text-primary" : "text-muted-foreground"
        )}>
          {translate("common.household")}
        </span>
      )}
      
      <button
        onClick={handleToggle}
        className={cn(
          "relative h-7 w-14 rounded-full transition-colors duration-300",
          mode === 'agriculture' 
            ? "bg-nature-100 dark:bg-nature-900/30" 
            : "bg-primary/20 dark:bg-primary/30"
        )}
      >
        <span className={cn(
          "absolute w-6 h-6 rounded-full flex items-center justify-center transition-all duration-300",
          "top-0.5 shadow-sm",
          mode === 'agriculture' 
            ? "translate-x-8 bg-nature-600 text-white" 
            : "translate-x-0.5 bg-primary text-primary-foreground"
        )}>
          {mode === 'agriculture' ? (
            <Tractor size={14} className="text-white" />
          ) : (
            <Home size={14} className="text-white" />
          )}
        </span>
      </button>
      
      {showLabels && (
        <span className={cn(
          "text-sm font-medium transition-colors",
          mode === 'agriculture' ? "text-nature-600" : "text-muted-foreground"
        )}>
          {translate("common.agriculture")}
        </span>
      )}
    </div>
  );
};

export default SectorToggle;
